import { Checkbox } from '@/components/forms/Checkbox';
import { Toggle } from '@/components/forms/Toggle';
import { getTimeframeLabel, type TimeframeEnumValue } from '@squaredup/timeframes';
import { Code } from 'components/forms/code/Code';
import Field from 'components/forms/field/Field';
import type { FC } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { type DataStreamSchemaFormData } from './useSaveAsDataStream';

interface SaveAsDataStreamFormProps {
    allowSavingCustomMetadata: boolean;
    allowSavingShaping: boolean;
    availableTimeframes: TimeframeEnumValue[];
    form: UseFormReturn<DataStreamSchemaFormData>;
}

export const SaveAsDataStreamForm: FC<SaveAsDataStreamFormProps> = ({
    allowSavingCustomMetadata,
    allowSavingShaping,
    availableTimeframes,
    form
}) => {
    const isRestrictingTimeframes = form.watch('restrictTimeframes');
    const isUiConfigEnabled = form.watch('includeUI');

    return (
        <div className='flex flex-col flex-1 min-h-0 gap-6 px-8 py-4 overflow-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-statusUnknownPrimary'>
            <Field.Input
                type='text'
                name='displayName'
                label='Data stream name'
                validation={{ required: true, minLength: 3, maxLength: 128 }}
            ></Field.Input>

            {allowSavingCustomMetadata && (
                <div className='flex flex-col items-start gap-1'>
                    <label
                        htmlFor='includeMetadata'
                        className='flex items-center gap-2 font-medium cursor-pointer select-none'
                    >
                        Include custom columns
                        <Controller
                            name='includeMetadata'
                            render={({ field: { value, onChange } }) => (
                                <Toggle id='includeMetadata' checked={value} onCheckedChange={onChange} />
                            )}
                        />
                    </label>

                    <span className='block text-textIncomplete'>
                        Include the custom columns in your saved data stream
                    </span>
                </div>
            )}

            {availableTimeframes.length > 0 && (
                <div className='flex flex-col items-start gap-1'>
                    <label
                        htmlFor='restrictTimeframes'
                        className='flex items-center gap-4 font-medium cursor-pointer select-none'
                    >
                        <span>Restrict available timeframes</span>

                        <Controller
                            name='restrictTimeframes'
                            render={({ field: { value, onChange } }) => (
                                <Toggle id='restrictTimeframes' checked={value} onCheckedChange={onChange} />
                            )}
                        />
                    </label>

                    <span className='block text-textIncomplete'>
                        Only allow a set list of timeframes for the data stream
                    </span>
                </div>
            )}

            {isRestrictingTimeframes && (
                <Controller
                    name='allowedTimeframes'
                    render={({ field: { value, onChange } }) => {
                        if (availableTimeframes.length === 0) {
                            return <></>;
                        }

                        return (
                            <div className='grid grid-cols-6 gap-x-4 gap-y-2' aria-label='Allowed timeframes'>
                                {availableTimeframes.map((timeframe) => {
                                    const fieldName = `allowedTimeframes-${timeframe}`;

                                    return (
                                        <label
                                            key={timeframe}
                                            className='flex items-center gap-2 font-medium cursor-pointer select-none'
                                        >
                                            <Checkbox
                                                name={fieldName}
                                                checked={value?.includes(timeframe)}
                                                onCheckedChange={() => {
                                                    const currentValue = value ? [...value] : [];
                                                    const newValue = currentValue.includes(timeframe)
                                                        ? [...currentValue.filter((t) => t !== timeframe)]
                                                        : [...currentValue, timeframe];
                                                    onChange(newValue);
                                                }}
                                            />
                                            <span>{getTimeframeLabel(timeframe)}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        );
                    }}
                />
            )}

            {allowSavingShaping && (
                <div className='flex flex-col items-start gap-1'>
                    <label
                        htmlFor='includeShaping'
                        className='flex items-center gap-2 font-medium cursor-pointer select-none'
                    >
                        Include shaping configuration
                        <Controller
                            name='includeShaping'
                            render={({ field: { value, onChange } }) => (
                                <Toggle id='includeShaping' checked={value} onCheckedChange={onChange} />
                            )}
                        />
                    </label>

                    <span className='block text-textIncomplete'>
                        Include your shaping configuration - this will be used as the shaping defaults for other users
                    </span>
                </div>
            )}

            <div className='flex flex-col items-start gap-1'>
                <label htmlFor='includeUI' className='flex items-center gap-2 font-medium cursor-pointer select-none'>
                    Include configuration UI
                    <Controller
                        name='includeUI'
                        render={({ field: { value, onChange } }) => (
                            <Toggle id='includeUI' checked={value} onCheckedChange={onChange} />
                        )}
                    />
                </label>

                <span className='block text-textIncomplete'>
                    Include UI to allow users to configure this data stream
                </span>
            </div>

            {isUiConfigEnabled && (
                <>
                    <div>
                        <label htmlFor='uiConfig' className='block mb-2 font-medium cursor-pointer select-none'>
                            UI Config
                        </label>

                        <Code name='uiConfig' defaultValue={[]} heightConstraints={{ min: 300 }} />
                    </div>

                    <div>
                        <label htmlFor='uiConfigMapping' className='block mb-2 font-medium cursor-pointer select-none'>
                            UI Config mapping
                        </label>

                        <Code name='uiConfigMapping' defaultValue={{}} heightConstraints={{ min: 150 }} />
                    </div>
                </>
            )}
        </div>
    );
};
